/**
 * HR Signup — Velaigal rebrand.
 *
 * Free account creation only. Company details, billing, and everything
 * else that used to gate this behind Razorpay now live in the HR profile
 * flow post-signup — this page collects name, work email, and password,
 * then submits straight to the session API.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { signup, resendVerification } from '../../lib/authApi';
import { ApiError } from '../../lib/api';
import Wordmark from '../../components/Brand/Wordmark';

// Module-scope shell — must NOT be defined inside HRSignup. A component
// declared inside another component's body gets a new function identity on
// every render, so React treats it as a brand-new type and unmounts/remounts
// the whole subtree each time — including every input — which drops focus on
// every keystroke. Both call sites below use the same max-w-xl width, so
// there's no prop to thread through here.
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen w-full bg-cream">
    <header className="border-b border-hairline bg-cream">
      <div className="max-w-7xl mx-auto px-8 h-[72px] flex items-center justify-between">
        <Link to="/" aria-label="Velaigal home" className="text-ink no-underline">
          <Wordmark variant="lockup" size="md" animateOnMount />
        </Link>
        <span className="text-sm text-ink-tertiary">
          Already have an account?{' '}
          <Link
            to="/login"
            className="velaigal-link font-medium"
          >
            Sign in
          </Link>
        </span>
      </div>
    </header>
    <main className="max-w-xl mx-auto px-8 py-16 lg:py-24">{children}</main>
  </div>
);

const HRSignup: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 10) newErrors.password = 'At least 10 characters';

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!validateForm()) return;

    setBusy(true);
    try {
      const res = await signup({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: 'hr',
      });
      setSubmitted(res.message);
    } catch (err) {
      if (err instanceof ApiError && err.fields) setErrors(err.fields);
      else toast.error(err instanceof ApiError ? err.message : 'Signup failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await resendVerification(formData.email);
      toast.success(res.message);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send verification email.');
    } finally {
      setResending(false);
    }
  };

  // ---------- Check-your-email screen ----------

  if (submitted) {
    return (
      <Shell>
        <div className="text-center">
          <Wordmark variant="mark" size="lg" />
          <h1
            className="font-display text-4xl font-medium text-ink mt-8 leading-tight"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Check your email
          </h1>
          <span aria-hidden="true" className="block w-12 h-px bg-saffron mx-auto mt-6" />
          <p className="mt-6 text-base text-ink-secondary max-w-md mx-auto leading-relaxed">
            {submitted}
          </p>

          <div className="mt-10 flex flex-col gap-3 max-w-xs mx-auto">
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="btn-secondary w-full"
            >
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>
            <Link to="/login" className="btn-primary w-full">
              Go to sign in
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  // ---------- Form screen ----------

  return (
    <Shell>
      <div className="mb-10">
        <span className="text-[11px] uppercase tracking-widest text-ink-tertiary">
          Recruiter signup
        </span>
        <h1
          className="font-display text-4xl font-medium text-ink mt-4 leading-tight tracking-tight velaigal-headline-hover"
          style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
        >
          Start hiring on Velaigal.
        </h1>
        <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
        <p className="mt-6 text-base text-ink-secondary leading-relaxed">
          Create your account now — your company details come next, right
          after you verify your email.
        </p>
      </div>

      <form onSubmit={handleFormSubmit} className="space-y-5">
        {[
          { name: 'name', label: 'Your name', placeholder: 'Your full name', type: 'text', autoComplete: 'name' },
          { name: 'email', label: 'Work email', placeholder: 'priya@orbit.com', type: 'email', autoComplete: 'email' },
          { name: 'password', label: 'Password', placeholder: 'At least 10 characters', type: 'password', autoComplete: 'new-password' },
          { name: 'confirmPassword', label: 'Confirm password', placeholder: 'Repeat password', type: 'password', autoComplete: 'new-password' },
        ].map((field) => (
          <div key={field.name} className="space-y-2">
            <label htmlFor={field.name} className="block text-sm font-medium text-ink">
              {field.label}
            </label>
            <input
              id={field.name}
              name={field.name}
              type={field.type}
              required
              value={(formData as Record<string, string>)[field.name]}
              onChange={handleChange}
              placeholder={field.placeholder}
              aria-invalid={!!errors[field.name]}
              className="input"
              style={errors[field.name] ? { borderColor: 'var(--color-error)' } : undefined}
              autoComplete={(field as Record<string, string>).autoComplete}
            />
            {errors[field.name] && (
              <p role="alert" className="text-xs text-error font-medium">
                {errors[field.name]}
              </p>
            )}
          </div>
        ))}

        <button
          type="submit"
          disabled={busy}
          className="btn-primary w-full mt-8"
        >
          {busy ? (
            <>
              <span
                className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
                style={{
                  borderColor: 'rgba(244, 240, 232, 0.35)',
                  borderTopColor: 'var(--color-cream)',
                }}
                aria-hidden="true"
              />
              <span>Creating account</span>
            </>
          ) : (
            'Create account'
          )}
        </button>
      </form>
    </Shell>
  );
};

export default HRSignup;
